/**
 * Alert — the single inline banner for blocking errors / warnings / info.
 *
 * Token-based (adapts to dark + light) and always `role="alert"`. The default
 * `danger` tone reproduces the error-banner markup that the route screens used to
 * hand-roll, so it is a drop-in replacement.
 */
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';

import { cn } from './cn.js';

export type AlertTone = 'danger' | 'warn' | 'info';

const toneClasses: Record<AlertTone, string> = {
  danger: 'border-danger bg-danger-soft text-danger',
  warn: 'border-warn bg-warn-soft text-warn',
  info: 'border-primary bg-primary-soft text-primary',
};

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  tone?: AlertTone;
  children: ReactNode;
}

export function Alert({ tone = 'danger', className, children, ...props }: AlertProps): ReactElement {
  return (
    <div
      role="alert"
      className={cn('rounded-md border px-3 py-2 text-sm', toneClasses[tone], className)}
      {...props}
    >
      {children}
    </div>
  );
}
